<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-8">
      <div class="flex items-center gap-6">
        <InfuseFilterCard
          id="connected-charging-stations"
          v-permission="{ id: statisticsPermissions.GET_DEVICE_STATS }"
          :value="stats.chargingStations.connected"
          :total="stats.chargingStations.total"
          :label="() => t('e_connector.dashboard.connectedChargingStations')"
          :active="true"
          :compact="false"
          :fixed-width="false"
          type="display"
        />
        <InfuseFilterCard
          id="connected-central-systems"
          v-permission="{ id: statisticsPermissions.GET_DEVICE_STATS }"
          :value="stats.centralSystems.connected"
          :total="stats.centralSystems.total"
          :label="() => t('e_connector.dashboard.connectedCentralSystems')"
          :active="true"
          :compact="false"
          :fixed-width="false"
          type="display"
        />
      </div>

      <div
        v-permission="{ id: statisticsPermissions.GET_MESSAGE_STATS }"
        class="grid gap-6 md:grid-cols-1 lg:grid-cols-2"
      >
        <div class="rounded-md border p-4">
          <MessagesLineChart
            id="messages"
            :title="t('e_connector.dashboard.charts.messages.title')"
            :subtitle="t('e_connector.dashboard.charts.messages.subtitle')"
            :series="messageSeries"
          />
        </div>
        <div v-permission="{ id: statisticsPermissions.GET_DEVICE_STATS }" class="flex gap-6">
          <div class="rounded-md border p-4 w-full">
            <ConnectionsPieChart
              id="connections-charging-stations"
              :title="t('e_connector.dashboard.charts.connections.chargingStations.title')"
              :subtitle="t('e_connector.dashboard.charts.connections.chargingStations.subtitle')"
              :series="connectionSeriesChargingStations"
            />
          </div>
          <div class="rounded-md border p-4 w-full">
            <ConnectionsPieChart
              id="connections-central-systems"
              :title="t('e_connector.dashboard.charts.connections.centralSystems.title')"
              :subtitle="t('e_connector.dashboard.charts.connections.centralSystems.subtitle')"
              :series="connectionSeriesCentralSystems"
            />
          </div>
        </div>
      </div>
    </div>
  </InfusePageLayout>
</template>

<script lang="ts" setup>
import { InfusePageLayout } from '@infuse/ui/layouts';
import { useI18n } from 'vue-i18n';
import { InfuseFilterCard } from '@infuse/ui/components';
import { computed, ComputedRef, onBeforeUnmount, onMounted } from 'vue';
import { StatisticMessage } from '@infuse/e-connector/dashboard/domain';
import { SeriesOptionsType } from 'highcharts';
import { MessageType } from '@infuse/e-connector/message/domain';
import { ConnectionsPieChart, MessagesLineChart } from '@infuse/e-connector/dashboard/ui';
import { useStatistics } from '@infuse/e-connector/dashboard/queries';
import { statisticsPermissions } from '@infuse/api/e-connector/statistics';
import { vPermission } from '@infuse/iam/permission/directives';
const { t } = useI18n();
const { statistics: stats } = useStatistics();
let refreshTimer: number | undefined;

onMounted(async () => {
  // TODO investigate performance impact.
  // refreshTimer = window.setInterval(() => refetch(), 5000); // refresh chart data every 5 seconds
});

onBeforeUnmount(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }
});

const messageSeries: ComputedRef<SeriesOptionsType[]> = computed(() => {
  const { requests, responses, errors } = groupByMessageTypeAndMap(stats.value.messageStatsLast5Hours);
  return [
    {
      name: t('e_connector.dashboard.charts.messages.requests'),
      data: requests,
      className: 'series-requests'
    },
    {
      name: t('e_connector.dashboard.charts.messages.responses'),
      data: responses,
      className: 'series-responses'
    },
    {
      name: t('e_connector.dashboard.charts.messages.errors'),
      data: errors,
      className: 'series-errors'
    }
  ] as SeriesOptionsType[];
});

const connectionSeriesChargingStations: ComputedRef<SeriesOptionsType[]> = computed(() => {
  return [
    {
      name: t('e_connector.dashboard.charts.connections.chargingStationsSeries'),
      data: [
        {
          name: t('e_connector.dashboard.charts.connections.connected'),
          y: stats.value.chargingStations.connected,
          className: 'series-connected'
        },
        {
          name: t('e_connector.dashboard.charts.connections.inactive'),
          y: stats.value.chargingStations.total - stats.value.chargingStations.connected,
          className: 'series-inactive'
        }
      ]
    }
  ] as SeriesOptionsType[];
});

const connectionSeriesCentralSystems: ComputedRef<SeriesOptionsType[]> = computed(() => {
  return [
    {
      name: t('e_connector.dashboard.charts.connections.centralSystemsSeries'),
      data: [
        {
          name: t('e_connector.dashboard.charts.connections.connected'),
          y: stats.value.centralSystems.connected,
          className: 'series-connected'
        },
        {
          name: t('e_connector.dashboard.charts.connections.inactive'),
          y: stats.value.centralSystems.total - stats.value.centralSystems.connected,
          className: 'series-inactive'
        }
      ]
    }
  ] as SeriesOptionsType[];
});

function groupByMessageTypeAndMap(messages: StatisticMessage[]) {
  const requests = [];
  const responses = [];
  const errors = [];

  for (const stat of messages) {
    const entry = [Math.floor(+stat.date / 1000) * 1000, stat.count]; // round to whole seconds

    switch (stat.messageType) {
      case MessageType.REQUEST:
        requests.push(entry);
        break;
      case MessageType.RESPONSE:
        responses.push(entry);
        break;
      case MessageType.ERROR:
        errors.push(entry);
        break;
    }
  }

  const sortFn = (a: number[], b: number[]) => a[0] - b[0];
  requests.sort(sortFn); // highcharts requires sorted data
  responses.sort(sortFn);
  errors.sort(sortFn);
  return { requests, responses, errors };
}
</script>
